import { useState } from "react";
import * as XLSX from "xlsx";
import { FiX, FiUpload, FiCheck, FiAlertTriangle, FiDownload } from "react-icons/fi";
import { apiPost } from "../api";
import { Btn } from "./KioskUI";

/**
 * Generic CSV/Excel bulk-import modal.
 * `columns`: [{ key, label, required }] — key must match the row field the backend expects.
 * `endpoint`: bulk POST endpoint, e.g. "/api/inventory/customers/bulk".
 */
export default function BulkImport({ title, columns, endpoint, token, onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [fileName, setFileName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const parseFile = (file) => {
    setError(""); setResult(null); setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "binary" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        const mapped = data
          .map((r) => {
            const row = {};
            columns.forEach(({ key, label }) => {
              const raw = r[label] ?? r[key] ?? "";
              row[key] = typeof raw === "string" ? raw.trim() : raw;
            });
            return row;
          })
          .filter((r) => columns.some((c) => !c.required) || r[columns.find((c) => c.required)?.key]);
        setRows(mapped);
      } catch {
        setError("Could not read this file. Use a .csv or .xlsx exported from Excel/Sheets.");
        setRows(null);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleImport = async () => {
    if (!rows || rows.length === 0) return;
    setSubmitting(true); setError("");
    try {
      const res = await apiPost(endpoint, { rows }, token);
      const data = await res.json();
      if (res.ok || res.status === 400) {
        setResult(data);
        if (data.created?.length) onDone?.();
      } else {
        setError(data.error || "Import failed.");
      }
    } catch {
      setError("Cannot connect to server.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([Object.fromEntries(columns.map((c) => [c.label, ""]))]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, `${title.toLowerCase().replace(/\s+/g, "-")}-template.xlsx`);
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)", width: 480, padding: "24px 26px 22px", maxHeight: "86vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 19, fontWeight: 800, color: "var(--ink)" }}>Import {title}</div>
          <button onClick={onClose} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 6, cursor: "pointer" }}>
            <FiX size={15} color="var(--sub)" />
          </button>
        </div>
        <div style={{ fontSize: 13, color: "var(--sub)", marginBottom: 14 }}>
          Upload a CSV or Excel file. Columns: {columns.map((c) => c.label).join(", ")}.
        </div>

        {!result && (
          <Btn kind="ghost" onClick={downloadTemplate} style={{ width: "100%", justifyContent: "center", height: 42, marginBottom: 14 }}>
            <FiDownload size={15} /> Download template
          </Btn>
        )}

        {!result && (
          <>
            <label style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, border: "1.5px dashed var(--line)", borderRadius: "var(--r-md)", padding: "28px 14px", cursor: "pointer", background: "var(--faint)" }}>
              <FiUpload size={22} color="var(--n-400)" />
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink)" }}>{fileName || "Choose a .csv or .xlsx file"}</span>
              <span style={{ fontSize: 12, color: "var(--sub)" }}>{rows ? `${rows.length} row${rows.length !== 1 ? "s" : ""} detected` : "Click to browse"}</span>
              <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => e.target.files[0] && parseFile(e.target.files[0])} style={{ display: "none" }} />
            </label>

            {error && (
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 12, color: "var(--danger)", fontSize: 12.5, fontWeight: 600 }}>
                <FiAlertTriangle size={14} /> {error}
              </div>
            )}

            <Btn kind="pri" onClick={handleImport} disabled={!rows || rows.length === 0 || submitting}
              style={{ width: "100%", justifyContent: "center", height: 48, marginTop: 16 }}>
              {submitting ? "Importing…" : `Import ${rows ? rows.length : ""} row${rows && rows.length !== 1 ? "s" : ""}`}
            </Btn>
          </>
        )}

        {result && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--success)", fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
              <FiCheck size={16} /> {result.created?.length || 0} imported
            </div>
            {result.errors?.length > 0 && (
              <div style={{ background: "var(--faint)", border: "1px solid var(--line)", borderRadius: "var(--r-md)", padding: "10px 12px", maxHeight: 200, overflowY: "auto" }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--danger)", marginBottom: 6 }}>{result.errors.length} row(s) skipped</div>
                {result.errors.map((e, i) => (
                  <div key={i} style={{ fontSize: 12, color: "var(--sub)", marginBottom: 4 }}>
                    Row {e.row} ({e.name || "no name"}): {Object.entries(e.errors || {}).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`).join("; ")}
                  </div>
                ))}
              </div>
            )}
            <Btn kind="pri" onClick={onClose} style={{ width: "100%", justifyContent: "center", height: 48, marginTop: 16 }}>Done</Btn>
          </div>
        )}
      </div>
    </div>
  );
}
